/**
 * BulkInvoiceActionsModal — aplica UMA escolha a todas as faturas selecionadas do passo 3.
 *
 * O passo 3 da Identificação chega a dezenas de linhas (uma por título da operação), e as três
 * escolhas de cada card — o destino das faturas abertas, a fonte do pagamento e o aval a devolver —
 * costumam ser a MESMA para o lote inteiro. Repeti-las card a card é o trabalho que este modal
 * elimina.
 *
 * Uma ação por vez, de propósito: um formulário com os três campos precisaria de um "não mexer
 * neste" para cada um, e "não mexer" é o estado que o usuário já tem ao não abrir o modal.
 *
 * ⚠️ As fontes vêm da ROTA (`business_properties.PAYMENT_SOURCES`), não de uma lista estática: o
 * dia em que o seed ganhar uma fonte, ela aparece aqui sem deploy do front.
 */
import { useState } from 'react';
import { FaTimes, FaLayerGroup } from 'react-icons/fa';
import { PaymentSourceOption } from '../services/billingApi';
import { PARENT_ACTION_ITEMS } from '../constants/paymentActionPromise';

/** O que o lote sabe fazer. Um `kind` por CAMPO do card, não uma ação por opção de select. */
export type BulkAction =
    | { kind: 'PROMISE'; promise: 'CANCEL' | 'UNCOLLECT' | 'KEEP' }
    | { kind: 'SOURCE'; alias: string }
    | { kind: 'REFUND'; on: boolean };

/** Prefixos do `value` do select — o rótulo é da tela, a chave é do código. */
const parseAction = (value: string): BulkAction | null => {
    const [kind, arg] = value.split(':');
    if (kind === 'promise') return { kind: 'PROMISE', promise: arg as 'CANCEL' | 'UNCOLLECT' | 'KEEP' };
    if (kind === 'source') return { kind: 'SOURCE', alias: arg };
    if (kind === 'refund') return { kind: 'REFUND', on: arg === 'on' };
    return null;
};

interface Props {
    /** quantas faturas o lote vai atingir — as SELECIONADAS, não as listadas */
    selectedCount: number;
    sources: PaymentSourceOption[];
    onApply: (action: BulkAction) => void;
    onClose: () => void;
    /**
     * Só a cobrança anterior: a tela de Pagamentos (AF-4062) decide apenas isso na aprovação, e
     * fonte e aval são da marcação.
     */
    promiseOnly?: boolean;
}

const BulkInvoiceActionsModal: React.FC<Props> = ({
    selectedCount, sources, onApply, onClose, promiseOnly = false,
}) => {
    const [value, setValue] = useState('');

    const apply = () => {
        const action = parseAction(value);
        if (action) onApply(action);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <FaLayerGroup className="text-blue-500" /> Ações em Lote
                    </h2>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes size={20} /></button>
                </div>

                <p className="text-xs text-gray-500 mb-3">
                    A ação escolhida vale para as <strong>{selectedCount} fatura(s)
                        selecionada(s)</strong>. Os demais campos de cada fatura ficam como estão.
                </p>

                <label className="block text-sm font-medium mb-1" htmlFor="acao-lote">Ação</label>
                <select id="acao-lote" value={value} onChange={e => setValue(e.target.value)}
                    className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm mb-3 dark:bg-slate-700 dark:text-white">
                    <option value="">Selecione</option>
                    {promiseOnly ? (
                        <optgroup label="Cobrança anterior">
                            {PARENT_ACTION_ITEMS.map(item => (
                                <option key={item.value} value={`promise:${item.value}`}>{item.label}</option>
                            ))}
                        </optgroup>
                    ) : (
                        <optgroup label="Demais faturas abertas">
                            <option value="promise:CANCEL">Cancelar</option>
                            <option value="promise:UNCOLLECT">Parar de Cobrar</option>
                            <option value="promise:KEEP">Manter como estão</option>
                        </optgroup>
                    )}
                    {!promiseOnly && <optgroup label="Fonte de Pagamento">
                        {sources.map(src => (
                            <option key={src.alias} value={`source:${src.alias}`}>
                                Fonte de Pagamento: {src.name}
                            </option>
                        ))}
                    </optgroup>}
                    {!promiseOnly && <optgroup label="Aval a Devolver">
                        <option value="refund:on">Marcar Aval a Devolver</option>
                        <option value="refund:off">Desmarcar Aval a Devolver</option>
                    </optgroup>}
                </select>

                {/* O usuário não vê a fonte de cada card daqui — sem este aviso ele leria o
                    "N atualizadas" como "todas", e as puladas passariam despercebidas. */}
                {value === 'refund:on' && (
                    <div className="px-3 py-2 mb-3 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-amber-800 dark:text-amber-300">
                        Só é aplicado nas faturas cuja Fonte de Pagamento admite devolução de aval.
                        As demais ficam como estão.
                    </div>
                )}

                <div className="flex gap-2 justify-end">
                    <button onClick={onClose}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                        Voltar
                    </button>
                    <button onClick={apply} disabled={!value}
                        className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                        Aplicar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BulkInvoiceActionsModal;
